import { useCallback, useMemo } from 'react';

import { useChatModeState } from '@atlassian/conversation-assistant-chat-prompt-input/useChatModeState';
import { useChatModeStoreActions } from '@atlassian/conversation-assistant-chat-prompt-input/useChatModeStoreActions';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { useEditorPlaceholder } from '@atlassian/conversation-assistant-ui-components/controllers/useEditorPlaceholder';

import { useChatStoreActions } from '../../controllers/chat-store/useChatStoreActions';
import { useConversation } from '../../controllers/conversation/useConversation';

const TEMPLATE_ID = 'rovo-chat-long-running-task-spotlight';
const FULL_PAGE_TEMPLATE_ID = 'rovo-chat-long-running-task-fullpage-banner';

type PlacementMessage = Record<string, unknown>;

type MaxSideNavBannerMessageProps = {
	onTryMax: () => void;
};

type MaxSideNavBannerMessage = {
	templateId: typeof TEMPLATE_ID;
	fullPageTemplateId: typeof FULL_PAGE_TEMPLATE_ID;
	isEligible: (message: PlacementMessage) => boolean;
	maxSideNavBannerMessageProps: MaxSideNavBannerMessageProps;
};

export const useMaxSideNavBannerMessage = (): MaxSideNavBannerMessage => {
	const { conversation } = useConversation({});
	const { setSelectedConversation } = useChatStoreActions();
	const conversationId = conversation?.id ?? '';
	const isEmptyConversation = conversation?.id === EMPTY_CONVERSATION_ID;
	const { taskModeEnabled } = useChatModeState(conversationId);
	const { setReasoningMode } = useChatModeStoreActions();
	const { insertPlaceholder } = useEditorPlaceholder();

	const isEligible = useCallback(
		(): boolean => !taskModeEnabled && isEmptyConversation,
		[taskModeEnabled, isEmptyConversation],
	);
	const onTryMax = useCallback(() => {
		setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID });
		setReasoningMode(EMPTY_CONVERSATION_ID, 'task');
		insertPlaceholder(
			"Summarize my team's recent activity across Jira and Confluence and generate a beautiful html report",
		);
	}, [insertPlaceholder, setReasoningMode, setSelectedConversation]);
	const maxSideNavBannerMessageProps = useMemo(() => ({ onTryMax }), [onTryMax]);

	return {
		templateId: TEMPLATE_ID,
		fullPageTemplateId: FULL_PAGE_TEMPLATE_ID,
		isEligible,
		maxSideNavBannerMessageProps,
	};
};
